import React from "react";
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from "recharts";

const CADENCE_STATES = [
  { key: "completed", label: "Completed", color: "#198754" },
  { key: "failed",    label: "Failed",    color: "#dc3545" },
];

const SIZE_COLOR = "#7c3aed";

function LinechartDashboard({ timeRange = "all", data = [] }) {
  const hasSizeData = data.some(d => d.sizeGB != null && d.sizeGB > 0);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const nonZero = payload.filter(e => e.value > 0 || e.dataKey === 'sizeGB');
    if (!nonZero.length) return null;

    const runEntries  = nonZero.filter(e => e.dataKey !== 'sizeGB' && e.value > 0);
    const sizeEntry   = nonZero.find(e => e.dataKey === 'sizeGB');
    const totalRuns   = runEntries.reduce((s, e) => s + e.value, 0);

    return (
      <div style={{ background: "white", padding: "10px", border: "1px solid #ccc", borderRadius: "4px", boxShadow: "0 2px 5px rgba(0,0,0,0.1)", minWidth: "180px" }}>
        <p style={{ fontWeight: "bold", margin: "0 0 6px 0", borderBottom: "1px solid #eee", paddingBottom: "4px" }}>{label}</p>
        {runEntries.map((entry, i) => (
          <p key={i} style={{ color: entry.color, margin: "3px 0", display: "flex", justifyContent: "space-between", gap: "12px" }}>
            <span style={{ fontWeight: 600 }}>{entry.name}</span>
            <span>{Number(entry.value).toLocaleString()}</span>
          </p>
        ))}
        {runEntries.length > 1 && (
          <p style={{ margin: "4px 0", display: "flex", justifyContent: "space-between", gap: "12px", fontWeight: 700, color: "#334155" }}>
            <span>Total Runs</span>
            <span>{totalRuns.toLocaleString()}</span>
          </p>
        )}
        {sizeEntry && sizeEntry.value > 0 && (
          <>
            <div style={{ borderTop: "1px solid #eee", margin: "6px 0 4px" }} />
            <p style={{ color: SIZE_COLOR, margin: "3px 0", display: "flex", justifyContent: "space-between", gap: "12px" }}>
              <span style={{ fontWeight: 600 }}>File Size</span>
              <span>{Number(sizeEntry.value).toFixed(2)} GB</span>
            </p>
          </>
        )}
      </div>
    );
  };

  const getXAxisConfig = () => {
    if (timeRange === "lastTwentyFourHours") return { angle: -35, height: 60, interval: 0 };
    if (timeRange === "currentMonth")        return { angle: -35, height: 60, interval: "preserveStartEnd" };
    if (timeRange === "6m" || timeRange === "1y") return { angle: -35, height: 60, interval: 0 };
    return { angle: -25, height: 55, interval: 0 };
  };

  const xAxisConfig = getXAxisConfig();

  return (
    <div style={{ width: "100%", height: "400px" }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 20, right: hasSizeData ? 70 : 20, bottom: 70, left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.7} />
          <XAxis
            dataKey="date"
            angle={xAxisConfig.angle}
            textAnchor="end"
            height={xAxisConfig.height}
            tickMargin={15}
            interval={xAxisConfig.interval}
          />

          {/* Left axis — run counts */}
          <YAxis
            yAxisId="runs"
            label={{ value: "Runs", angle: -90, position: "insideLeft", style: { textAnchor: "middle" }, offset: -5 }}
          />

          {/* Right axis — file size in GB (only when data exists) */}
          {hasSizeData && (
            <YAxis
              yAxisId="size"
              orientation="right"
              tickFormatter={(v) => `${v % 1 === 0 ? v : v.toFixed(1)}`}
              label={{ value: "GB", angle: 90, position: "insideRight", style: { textAnchor: "middle" }, offset: 15 }}
            />
          )}

          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ paddingTop: "10px", bottom: -10, left: 25, width: "90%" }} />

          {CADENCE_STATES.map((s, i) => (
            <Bar
              key={s.key}
              yAxisId="runs"
              dataKey={s.key}
              name={s.label}
              fill={s.color}
              stackId="cadence"
              radius={i === CADENCE_STATES.length - 1 ? [2, 2, 0, 0] : 0}
            />
          ))}

          {hasSizeData && (
            <Bar
              yAxisId="size"
              dataKey="sizeGB"
              name="File Size (GB)"
              fill={SIZE_COLOR}
              stackId="size"
              radius={[2, 2, 0, 0]}
              opacity={0.85}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export default LinechartDashboard;
